import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from 'api';
import { useToast } from 'hooks/useToast';
import type { Flow1HiringAssistantPayload, HiringAssistantOption } from './types';

/**
 * Interface for CV comparison response (Flow 1)
 */
export interface CVComparisonCandidate {
  name: string;
  email?: string;
  key_strengths: string[];
  risks_or_gaps: string[];
  fit_score: number;
  recommendation: string;
}

export interface CVComparisonUIHints {
  show_upload?: boolean;
  show_analysis_preview?: boolean;
  analysis_preview?: CVComparisonCandidate[];
  show_summary?: boolean;
  summary_data?: CVComparisonCandidate[];
  disclaimer?: string;
}

export interface CVComparisonResponse {
  message: string;
  options: HiringAssistantOption[];
  ui_hints: CVComparisonUIHints;
}

export interface CVComparisonFullResponse {
  assistant_id: number;
  response: CVComparisonResponse;
  current_state: string;
  conversation_history: any[];
  flow_type: string;
}

/**
 * Hook for uploading CVs in Flow 1
 */
export const useUploadCVs = (assistantId: number | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (files: { content: string }[]) => {
      if (!assistantId) {
        throw new Error('Assistant ID is required');
      }
      const response = await apiClient.post<CVComparisonFullResponse>(
        '/assessment/hiring-assistant/',
        {
          user_input: 'upload',
          assistant_id: assistantId,
          files: files,
        }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cvUpload', assistantId] });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to upload CVs';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    response: mutation.data,
    isLoading: mutation.isPending,
    error: mutation.error,
    uploadCVs: mutation.mutate,
  };
};

/**
 * Generic hook for the hiring assistant API
 */
export const useFlow1HiringAssistant = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  const mutation = useMutation({
    mutationFn: async (payload: Flow1HiringAssistantPayload) => {
      const response = await apiClient.post('/assessment/hiring-assistant/', payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['flow1HiringAssistant', assessmentId] });
      queryClient.invalidateQueries({ queryKey: ['conversationSessions'] });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to send request';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    response: mutation.data,
    loading: mutation.isPending,
    error: mutation.error,
    sendRequest: mutation.mutate,
  };
};
